'use client'

import { useEffect, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useApp } from '@/context/AppContext'
import { useLang } from '@/context/LangContext'
import { api } from '@/lib/api'
import { ArrowLeft, Plus, Trash2, Eye, EyeOff, X, Users, Store } from 'lucide-react'
import Link from 'next/link'

export default function SettingsPage() {
  const { session, logout } = useApp()
  const { t } = useLang()
  const router = useRouter()

  const [users, setUsers] = useState<any[]>([])
  const [showAdd, setShowAdd] = useState(false)
  const [showPin, setShowPin] = useState(false)
  const [toast, setToast] = useState('')
  const [form, setForm] = useState({ name: '', pin: '', role: 'vendeur' })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!session) { router.replace('/login'); return }
    if (session.user.role !== 'patron') { router.replace('/dashboard'); return }
    load()
  }, [session])

  async function load() {
    const data = await api.getShopUsers(session!.shop.id)
    setUsers(data)
  }

  async function addUser() {
    if (!form.name || form.pin.length !== 4) return
    setSaving(true)
    await api.createUser({ shop_id: session!.shop.id, ...form })
    setForm({ name:'', pin:'', role:'vendeur' })
    setShowAdd(false); setSaving(false)
    showToast('Utilisateur ajouté ✓'); load()
  }

  async function deleteUser(id: string) {
    if (id === session?.user.id) { showToast('⚠️ Mich aamlek ta7dhef rouek!'); return }
    if (!confirm('Supprimer?')) return
    await api.deleteUser(id)
    showToast('Supprimé ✓'); load()
  }

  function showToast(msg: string) { setToast(msg); setTimeout(() => setToast(''), 2500) }

  const roleConfig: Record<string, { label: string; color: string }> = {
    patron:   { label:'👑 Patron',    color:'bg-yellow-100 text-yellow-700' },
    vendeur:  { label:'🛒 Vendeur',   color:'bg-blue-100 text-blue-700' },
    caissier: { label:'💰 Caissier',  color:'bg-green-100 text-green-700' },
  }

  return (
    <div className="min-h-screen pb-8">
      <div className="bg-white shadow-sm">
        <div className="flex items-center gap-3 px-4 pt-12 pb-4">
          <Link href="/dashboard" className="p-2 -ml-2 text-gray-600"><ArrowLeft className="w-6 h-6" /></Link>
          <h1 className="text-xl font-bold text-gray-900 flex-1">{t('settings_title')}</h1>
        </div>
      </div>

      <div className="px-4 pt-4 space-y-4">

        <div className="bg-white rounded-2xl p-4 shadow-sm border border-gray-100 flex items-center gap-3">
          <div className="w-12 h-12 bg-indigo-100 rounded-2xl flex items-center justify-center"><Store className="w-6 h-6 text-indigo-600" /></div>
          <div>
            <p className="font-bold text-gray-800">{session?.shop.name}</p>
            <p className="text-sm text-gray-400">{users.length} {t('role_vendeur').includes('بايع') ? 'مستخدم' : 'utilisateurs'}</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <div className="flex items-center gap-2"><Users className="w-5 h-5 text-indigo-500" /><h3 className="font-bold text-gray-800">Utilisateurs</h3></div>
            <button onClick={() => setShowAdd(true)} className="w-8 h-8 bg-indigo-600 rounded-xl flex items-center justify-center">
              <Plus className="w-4 h-4 text-white" />
            </button>
          </div>
          {users.map(user => (
            <div key={user.id} className="flex items-center gap-3 px-4 py-3 border-b border-gray-50 last:border-0">
              <div className="w-10 h-10 bg-gray-100 rounded-xl flex items-center justify-center font-bold text-gray-600">{user.name[0].toUpperCase()}</div>
              <div className="flex-1">
                <p className="font-semibold text-gray-800">{user.name}{user.id === session?.user.id && <span className="text-xs text-indigo-500 ml-1">(moi)</span>}</p>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${roleConfig[user.role]?.color}`}>{roleConfig[user.role]?.label}</span>
              </div>
              {user.id !== session?.user.id && (
                <button onClick={() => deleteUser(user.id)} className="p-2 text-red-400"><Trash2 className="w-4 h-4" /></button>
              )}
            </div>
          ))}
        </div>

        <button onClick={logout} className="w-full py-4 bg-red-50 text-red-600 rounded-2xl font-bold border border-red-200 active:scale-95">
          Se déconnecter
        </button>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-end">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowAdd(false)} />
          <div className="relative w-full bg-white rounded-t-3xl p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-gray-800">Ajouter utilisateur</h2>
              <button onClick={() => setShowAdd(false)} className="w-9 h-9 bg-gray-100 rounded-full flex items-center justify-center"><X className="w-5 h-5 text-gray-600" /></button>
            </div>
            <div className="space-y-3 mb-5">
              <input autoFocus value={form.name} onChange={e => setForm(f => ({...f,name:e.target.value}))} placeholder="Prénom..."
                className="w-full px-4 py-3 rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:border-indigo-500" />
              <div className="relative">
                <input type={showPin?'text':'password'} inputMode="numeric" maxLength={4}
                  value={form.pin} onChange={e => setForm(f => ({...f,pin:e.target.value.replace(/\D/g,'').slice(0,4)}))}
                  placeholder="PIN 4 chiffres..."
                  className="w-full px-4 py-3 pr-12 rounded-2xl border border-gray-200 bg-gray-50 outline-none focus:border-indigo-500 text-xl tracking-[0.5em]" />
                <button onClick={() => setShowPin(!showPin)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">
                  {showPin ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {['vendeur','caissier','patron'].map(r => (
                  <button key={r} onClick={() => setForm(f => ({...f,role:r}))}
                    className={`py-2.5 rounded-2xl text-sm font-semibold border-2 ${form.role===r?'border-indigo-500 bg-indigo-50 text-indigo-700':'border-gray-200 text-gray-600'}`}>
                    {roleConfig[r].label}
                  </button>
                ))}
              </div>
            </div>
            <button onClick={addUser} disabled={saving || !form.name || form.pin.length !== 4}
              className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold text-lg disabled:opacity-50">
              {saving ? '...' : 'Ajouter ✓'}
            </button>
          </div>
        </div>
      )}

      {toast && (
        <div className="fixed bottom-8 left-1/2 -translate-x-1/2 z-50 bg-gray-800 text-white px-5 py-3 rounded-2xl shadow-xl font-semibold">{toast}</div>
      )}
    </div>
  )
}
